import { Link } from 'react-router-dom'
import { useListaDeGmuds } from '../../api/queries/gmuds'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { EtiquetaDeStatus } from '../../components/Etiqueta'
import { IconeSeta } from '../../components/Icones'
import { Cartao } from '../../components/ListaDeDados'
import { cn } from '../../lib/cn'
import { data, dataHoraPorExtenso } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { ResumoGmud } from '../../types/gmud'
import { ESPERANDO_ALGUEM, enderecoDaLista, filtrosDoRecorte } from './recortes'

/**
 * Cinco linhas, e não dez.
 *
 * O bloco é um atalho de leitura, não uma segunda listagem: passando disso ele empurra o resto do
 * painel para baixo da dobra e passa a competir com a tela que faz isso direito, com filtro,
 * ordenação e paginação.
 */
const QUANTAS = 5

/** Uma linha do atalho: código, estado, sistema e quando foi aberta. */
function Linha({ gmud }: { gmud: ResumoGmud }) {
  return (
    <li>
      <Link
        to={`/gmuds/${gmud.id}`}
        className={cn(
          'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg px-2 py-3 transition-colors',
          'hover:bg-niteroi-50 focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
        )}
      >
        <span className="font-semibold text-niteroi-700">{gmud.codigo}</span>
        <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />
        <span className="min-w-0 flex-1 truncate text-sm text-slate-600">
          {gmud.sistema ?? TEXTOS.naoInformado}
        </span>
        <time
          dateTime={gmud.criadoEm}
          title={dataHoraPorExtenso(gmud.criadoEm)}
          className="text-xs tabular-nums text-slate-500"
        >
          Aberta em {data(gmud.criadoEm)}
        </time>
      </Link>
    </li>
  )
}

/**
 * As solicitações mais recentes que dependem de um próximo passo.
 *
 * O título diz "esperando alguém", e não "esperando você", porque é a verdade do que está na tela:
 * a consulta é por ESTADO, não por pessoa — a API tem o recorte `minhas`, mas ele ainda não existe
 * nos filtros do frontend. Prometer uma caixa de entrada pessoal e entregar a lista de todo mundo
 * é o tipo de rótulo que faz o usuário parar de acreditar no painel inteiro.
 *
 * Nada aqui decide o que a pessoa pode fazer: quem faz isso é `/gmuds/{id}/transicoes-possiveis`,
 * na tela de detalhe. Este bloco só encurta o caminho até lá.
 *
 * @returns o cartão com o atalho
 */
export function EsperandoAlguem() {
  const { data: pagina, isPending, isError, error } = useListaDeGmuds(filtrosDoRecorte(ESPERANDO_ALGUEM, QUANTAS))

  return (
    <Cartao
      titulo={ESPERANDO_ALGUEM.rotulo}
      descricao="Não é a sua caixa de entrada: o que você pode fazer em cada uma aparece ao abri-la."
      acao={
        <Link
          to={enderecoDaLista(ESPERANDO_ALGUEM)}
          className="inline-flex items-center gap-1 text-sm font-medium text-niteroi-700 hover:text-niteroi-900"
        >
          Ver todas
          <IconeSeta className="h-4 w-4" />
        </Link>
      }
    >
      {isPending && <Esqueleto linhas={4} />}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar as solicitações" />}

      {pagina && pagina.conteudo.length === 0 && (
        <p className="max-w-prose text-sm text-slate-600">
          Nenhuma solicitação parada agora. Quando alguma for enviada ao comitê, devolvida para
          ajuste ou aprovada sem ter sido implantada, ela aparece aqui.
        </p>
      )}

      {pagina && pagina.conteudo.length > 0 && (
        <ul className="-mx-2 divide-y divide-slate-100">
          {pagina.conteudo.map((gmud) => (
            <Linha key={gmud.id} gmud={gmud} />
          ))}
        </ul>
      )}
    </Cartao>
  )
}
